import {
  APIContext,
  APIError,
  defaultParseError,
  useClientPortalAPI,
  useFormikSubmit,
} from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  Container,
  Divider,
  Layout,
  PrimaryButton,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";

import { components } from "@certn/api/generated-schemas/client-portal";
import {
  EMPTY_SUCCESSFACTORS_INTEGRATION,
  SuccessFactorsIntegration,
  SuccessFactorsIntegrationContext,
} from "../SuccessFactorsIntegrationContext";
import { buildSuccessFactorsWebhookUrl } from "../webhookSample";
import { SuccessFactorsInboundSection } from "./SuccessFactorsInboundSection";
import { SuccessFactorsIntegrationStepHeader } from "./SuccessFactorsIntegrationStepHeader";
import { SuccessFactorsOutboundSection } from "./SuccessFactorsOutboundSection";
import { SuccessFactorsPackagesAndTeamsList } from "./SuccessFactorsPackagesAndTeamsList";
import {
  isSapWrappedSuccessFactorsPem,
  normalizeSuccessFactorsCertificatePem,
  normalizeSuccessFactorsPrivateKeyPem,
  parseSuccessFactorsCertificateFile,
  SuccessFactorsCertificateParseError,
} from "../parseSuccessFactorsCertificateFile";
import {
  isObfuscatedStoredSecret,
  maskedPemPreview,
  maskedPrivateKeyPreview,
} from "../successFactorsPemMask";
import {
  ClientParsedOutbound,
  SuccessFactorsSapCertificateFileInput,
} from "./SuccessFactorsSapCertificateFileInput";
import { successFactorsEditIntegrationStableKey } from "../successFactorsEditIntegrationStableKey";

export type { ClientParsedOutbound } from "./SuccessFactorsSapCertificateFileInput";

interface Props {
  initialValues?: components["schemas"]["AtsIntegrationDetail"];
  reload?: () => void;
  clientParsedOutbound?: ClientParsedOutbound | null;
  onClientParsedOutboundChange?: (parsed: ClientParsedOutbound | null) => void;
  certificateUploadStatus?: string | null;
  onCertificateUploadStatusChange?: (status: string | null) => void;
}

interface GeneratedWebhookCredentials {
  webhook_username: string;
  webhook_password: string;
  webhook_url: string;
}

type WebhookCredentialResponse = {
  id?: string;
  webhook_username?: string;
  webhook_password?: string;
  webhook_url?: string;
};

type TestConnectionResponse = {
  ok?: boolean;
  grant_type?: string;
  token_endpoint?: string;
  message?: string;
};

type SuccessFactorsRecord = NonNullable<
  components["schemas"]["AtsIntegrationDetail"]["successfactors_integration"]
> & {
  team_id?: string;
  company_id?: string;
  api_user?: string;
  x509_certificate?: string;
  saml_private_key?: string;
  has_saml_private_key?: boolean;
};

function webhookPathFromIntegration(
  integration:
    | components["schemas"]["AtsIntegrationDetail"]["successfactors_integration"]
    | undefined
) {
  if (!integration) {
    return "";
  }
  return (integration as WebhookCredentialResponse).webhook_url || "";
}

function formValuesFromDetail(
  detail?: components["schemas"]["AtsIntegrationDetail"]
): SuccessFactorsIntegration {
  const sf = detail?.successfactors_integration as
    | SuccessFactorsRecord
    | null
    | undefined;
  return {
    report_url_type: (detail?.report_url_type || "CLIENT_PORTAL_URL") as string,
    team_id: sf?.team_id || "",
    company_id: sf?.company_id || "",
    api_user: sf?.api_user || "",
    success_factors_oauth_client_id: sf?.success_factors_oauth_client_id || "",
    success_factors_oauth_token_endpoint:
      sf?.success_factors_oauth_token_endpoint || "",
    odata_base_url: sf?.odata_base_url || "",
    x509_certificate: sf?.x509_certificate || "",
    saml_private_key: sf?.saml_private_key || "",
  };
}

function outboundPayload(values: SuccessFactorsIntegration) {
  return {
    company_id: values.company_id || "",
    api_user: values.api_user || "",
    success_factors_oauth_client_id:
      values.success_factors_oauth_client_id || "",
    success_factors_oauth_token_endpoint:
      values.success_factors_oauth_token_endpoint || "",
    odata_base_url: values.odata_base_url || "",
    x509_certificate: values.x509_certificate || "",
    saml_private_key: values.saml_private_key || "",
  };
}

function hasOutboundValues(values: SuccessFactorsIntegration) {
  return Object.values(outboundPayload(values)).some(
    (value) => value.trim() !== ""
  );
}

function optionalTeamIdPayload(teamId: string) {
  return teamId.trim() ? { team_id: teamId } : {};
}

function persistPayload(
  values: SuccessFactorsIntegration,
  clientParsed: ClientParsedOutbound | null | undefined
) {
  const outbound = hasOutboundValues(values)
    ? effectiveTestConnectionPayload(values, clientParsed)
    : {};
  return {
    ...optionalTeamIdPayload(values.team_id),
    ...outbound,
  };
}

function effectiveOutboundField(
  values: SuccessFactorsIntegration,
  clientParsed: ClientParsedOutbound | null | undefined,
  field: keyof Pick<
    SuccessFactorsIntegration,
    "x509_certificate" | "saml_private_key" | "company_id"
  >
): string {
  const fromValues = values[field].trim();
  if (fromValues) {
    return values[field];
  }
  const fromParsed = clientParsed?.[field]?.trim();
  if (fromParsed) {
    return clientParsed?.[field] ?? "";
  }
  return values[field];
}

function effectiveOutboundSecretField(
  values: SuccessFactorsIntegration,
  clientParsed: ClientParsedOutbound | null | undefined,
  field: "x509_certificate" | "saml_private_key"
): string {
  const fromParsed = clientParsed?.[field]?.trim();
  const fromValues = values[field].trim();
  if (
    fromParsed &&
    clientParsed &&
    (!fromValues ||
      isObfuscatedStoredSecret(fromValues) ||
      isSapWrappedSuccessFactorsPem(values[field]))
  ) {
    return clientParsed[field];
  }
  const effective = effectiveOutboundField(values, clientParsed, field);
  if (
    field === "x509_certificate" &&
    isSapWrappedSuccessFactorsPem(effective)
  ) {
    return normalizeSuccessFactorsCertificatePem(effective);
  }
  if (
    field === "saml_private_key" &&
    isSapWrappedSuccessFactorsPem(effective)
  ) {
    return normalizeSuccessFactorsPrivateKeyPem(effective);
  }
  return effective;
}

export function effectiveTestConnectionPayload(
  values: SuccessFactorsIntegration,
  clientParsed: ClientParsedOutbound | null | undefined
) {
  const base = outboundPayload(values);
  return {
    ...base,
    company_id: effectiveOutboundField(values, clientParsed, "company_id"),
    x509_certificate: effectiveOutboundSecretField(
      values,
      clientParsed,
      "x509_certificate"
    ),
    saml_private_key: effectiveOutboundSecretField(
      values,
      clientParsed,
      "saml_private_key"
    ),
  };
}

export function hasRequiredOutboundValues(
  values: SuccessFactorsIntegration,
  clientParsed: ClientParsedOutbound | null | undefined
): boolean {
  const payload = effectiveTestConnectionPayload(values, clientParsed);
  return [
    payload.company_id,
    payload.api_user,
    payload.success_factors_oauth_client_id,
    payload.success_factors_oauth_token_endpoint,
    payload.x509_certificate,
  ].every((value) => value.trim() !== "");
}

export function parseSuccessFactorsTestConnectionError(
  error: APIError
): string {
  const errorCode =
    typeof error.body.error === "string" ? error.body.error.trim() : "";
  const details =
    typeof error.body.details === "string" ? error.body.details.trim() : "";
  if (errorCode && details) {
    return `${errorCode}: ${details}`;
  }
  return defaultParseError(error) || error.detail;
}

function hasOutboundCertificateMaterial(
  values: SuccessFactorsIntegration,
  clientParsedOutbound: ClientParsedOutbound | null | undefined,
  storedSuccessFactors?: SuccessFactorsRecord | undefined
): boolean {
  if (clientParsedOutbound) {
    return Boolean(
      clientParsedOutbound.x509_certificate.trim() ||
        clientParsedOutbound.saml_private_key.trim()
    );
  }
  if (values.x509_certificate.trim() || values.saml_private_key.trim()) {
    return true;
  }
  if (storedSuccessFactors?.x509_certificate?.trim()) {
    return true;
  }
  return Boolean(
    storedSuccessFactors?.has_saml_private_key ||
      storedSuccessFactors?.saml_private_key?.trim()
  );
}

function readSuccessFactorsCertificateFile(
  file: File | undefined,
  onParsed: (
    result: ReturnType<typeof parseSuccessFactorsCertificateFile>,
    fileName: string
  ) => void,
  onError: (message: string) => void,
  onReading?: (fileName: string) => void
) {
  if (!file) {
    onError("No file was selected. Choose the SAP certificate file again.");
    return;
  }
  const fileName = file.name;
  onReading?.(fileName);
  const reader = new FileReader();
  const handleReadError = () => {
    onError(
      "Could not read this certificate file. Upload the file downloaded from SAP without opening it."
    );
  };
  reader.onload = () => {
    if (typeof reader.result !== "string") {
      handleReadError();
      return;
    }
    try {
      const parsed = parseSuccessFactorsCertificateFile(reader.result);
      onParsed(parsed, fileName);
    } catch (error) {
      if (error instanceof SuccessFactorsCertificateParseError) {
        onError(error.message);
        return;
      }
      onError(
        "Could not read this certificate file. Upload the file downloaded from SAP without opening it."
      );
    }
  };
  reader.onerror = handleReadError;
  reader.onabort = handleReadError;
  try {
    reader.readAsText(file);
  } catch {
    handleReadError();
  }
}

export function SuccessFactorsSetupStep({
  initialValues,
  reload,
  clientParsedOutbound: clientParsedOutboundProp,
  onClientParsedOutboundChange,
  certificateUploadStatus: certificateUploadStatusProp,
  onCertificateUploadStatusChange,
}: Props = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { domain } = useContext(APIContext);
  const context = useContext(SuccessFactorsIntegrationContext);
  const { integrationData: contextData, setIntegration } = context || {
    integrationData: null,
    setIntegration: () => {},
  };

  const isEditMode = !!initialValues?.id;
  const [generatedCredentials, setGeneratedCredentials] =
    useState<GeneratedWebhookCredentials | null>(null);
  const [createdIntegrationId, setCreatedIntegrationId] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const connectSucceededRef = useRef(false);
  const outboundSaveSucceededRef = useRef(false);
  const toastOutboundSavedRef = useRef(false);
  const sapCertificateInputRef = useRef<HTMLInputElement>(null);
  const sapCertificateFileInputId = useId();
  const [internalClientParsedOutbound, setInternalClientParsedOutbound] =
    useState<ClientParsedOutbound | null>(null);
  const [internalCertificateUploadStatus, setInternalCertificateUploadStatus] =
    useState<string | null>(null);
  const [certificateParseError, setCertificateParseError] = useState<
    string | null
  >(null);
  const clientParsedOutboundControlled =
    onClientParsedOutboundChange !== undefined;
  const clientParsedOutbound = clientParsedOutboundControlled
    ? clientParsedOutboundProp ?? null
    : internalClientParsedOutbound;
  const setClientParsedOutbound = useCallback(
    (parsed: ClientParsedOutbound | null) => {
      if (clientParsedOutboundControlled) {
        onClientParsedOutboundChange?.(parsed);
        return;
      }
      setInternalClientParsedOutbound(parsed);
    },
    [clientParsedOutboundControlled, onClientParsedOutboundChange]
  );
  const certificateUploadStatusControlled =
    onCertificateUploadStatusChange !== undefined;
  const certificateUploadStatus = certificateUploadStatusControlled
    ? certificateUploadStatusProp ?? null
    : internalCertificateUploadStatus;
  const setCertificateUploadStatus = useCallback(
    (status: string | null) => {
      if (certificateUploadStatusControlled) {
        onCertificateUploadStatusChange?.(status);
        return;
      }
      setInternalCertificateUploadStatus(status);
    },
    [certificateUploadStatusControlled, onCertificateUploadStatusChange]
  );
  const [needsManualCompanyId, setNeedsManualCompanyId] = useState(
    () => clientParsedOutbound?.needsManualCompanyId ?? false
  );
  const formikBagRef = useRef<FormikProps<SuccessFactorsIntegration> | null>(
    null
  );

  useEffect(() => {
    if (!clientParsedOutbound) {
      return;
    }
    const formik = formikBagRef.current;
    if (!formik) {
      return;
    }
    // Set only the parsed fields. This effect can run after other fields were
    // edited but before Formik re-rendered, so spreading `values` into
    // setValues would restore that stale snapshot and wipe those edits.
    const { values, setFieldValue } = formik;
    void setFieldValue(
      "x509_certificate",
      clientParsedOutbound.x509_certificate,
      false
    );
    void setFieldValue(
      "saml_private_key",
      clientParsedOutbound.saml_private_key,
      false
    );
    void setFieldValue(
      "company_id",
      clientParsedOutbound.needsManualCompanyId
        ? values.company_id || clientParsedOutbound.company_id
        : clientParsedOutbound.company_id || values.company_id
    );
    if (clientParsedOutbound.needsManualCompanyId) {
      setNeedsManualCompanyId(true);
    }
  }, [clientParsedOutbound]);

  const prevEditIntegrationKeyRef = useRef("");

  useEffect(() => {
    if (!isEditMode || !initialValues) {
      return;
    }
    const integrationKey =
      successFactorsEditIntegrationStableKey(initialValues);
    if (integrationKey === prevEditIntegrationKeyRef.current) {
      return;
    }
    prevEditIntegrationKeyRef.current = integrationKey;
    const formik = formikBagRef.current;
    if (!formik) {
      return;
    }
    const fromDetail = formValuesFromDetail(initialValues);
    const { values, setValues } = formik;
    const preferExistingMaterial = (
      current: string,
      parsed: string | undefined,
      fromServer: string
    ) => {
      if (parsed?.trim()) {
        if (
          !current.trim() ||
          isObfuscatedStoredSecret(current) ||
          current.trim() === fromServer.trim()
        ) {
          return parsed;
        }
      }
      if (current.trim()) {
        return current;
      }
      if (parsed?.trim()) {
        return parsed;
      }
      return fromServer;
    };
    void setValues({
      ...fromDetail,
      x509_certificate: preferExistingMaterial(
        values.x509_certificate,
        clientParsedOutbound?.x509_certificate,
        fromDetail.x509_certificate
      ),
      saml_private_key: preferExistingMaterial(
        values.saml_private_key,
        clientParsedOutbound?.saml_private_key,
        fromDetail.saml_private_key
      ),
      company_id: preferExistingMaterial(
        values.company_id,
        clientParsedOutbound?.company_id,
        fromDetail.company_id
      ),
    });
  }, [clientParsedOutbound, initialValues, isEditMode]);

  const integrationData = isEditMode
    ? formValuesFromDetail(initialValues)
    : contextData || EMPTY_SUCCESSFACTORS_INTEGRATION;

  const validationSchema = useMemo(
    () =>
      object().shape(
        isEditMode || currentStep >= 1
          ? { team_id: string().required().label(t("Default team")) }
          : {}
      ),
    [currentStep, isEditMode, t]
  );

  const {
    data: customerGroups,
    isLoading: isCustomerGroupsLoading,
    error: customerGroupsError,
    mutate: retryCustomerGroups,
  } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/customer-groups/"
  );

  const { submit: createIntegration, isSubmitting: isCreating } =
    useFormikSubmit<WebhookCredentialResponse>(
      "/api/internal/client-portal/ats-integrations/create/",
      {
        onSuccess: (data) => {
          if (data.id) {
            setCreatedIntegrationId(String(data.id));
          }
          setGeneratedCredentials({
            webhook_username: data.webhook_username || "",
            webhook_password: data.webhook_password || "",
            webhook_url: data.webhook_url || "",
          });
          showToast(
            t("Success, SAP SuccessFactors has been successfully enabled."),
            {
              type: "success",
            }
          );
        },
        showToastOnError: true,
      }
    );

  const integrationId = initialValues?.id || createdIntegrationId;
  const updateUrl = integrationId
    ? `/api/internal/client-portal/ats-integrations/${integrationId}/update/`
    : "";

  const { submit: updateIntegration, isSubmitting: isUpdating } =
    useFormikSubmit<WebhookCredentialResponse>(updateUrl, {
      onSuccess: (data) => {
        outboundSaveSucceededRef.current = true;
        if (data?.webhook_username && data?.webhook_password) {
          setGeneratedCredentials({
            webhook_username: data.webhook_username,
            webhook_password: data.webhook_password,
            webhook_url:
              data.webhook_url ||
              webhookPathFromIntegration(
                initialValues?.successfactors_integration
              ) ||
              "",
          });
          showToast(t("Webhook credentials regenerated"), {
            type: "success",
          });
        } else if (toastOutboundSavedRef.current) {
          showToast(t("Outbound SAML settings saved"), {
            type: "success",
          });
        }
        reload?.();
      },
      showToastOnError: true,
    });

  const { submit: testConnection, isSubmitting: isTestingConnection } =
    useFormikSubmit<TestConnectionResponse>(
      "/api/internal/client-portal/ats-integrations/successfactors/test-connection/",
      {
        onSuccess: (data) => {
          connectSucceededRef.current = true;
          showToast(
            data?.message || t("SAML certificate and private key are valid."),
            {
              type: "success",
            }
          );
        },
        parseError: parseSuccessFactorsTestConnectionError,
        showToastOnError: true,
      }
    );

  const handleRegenerate = useCallback(
    async (
      values: SuccessFactorsIntegration,
      formikHelpers: FormikHelpers<SuccessFactorsIntegration>
    ) => {
      await updateIntegration(
        {
          report_url_type: (values.report_url_type ||
            "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
          successfactors_integration: {
            regenerate_webhook_credentials: true,
          },
        } as components["schemas"]["AtsIntegrationUpdate"],
        formikHelpers
      );
    },
    [updateIntegration]
  );

  const handleSaveOutbound = useCallback(
    async (
      values: SuccessFactorsIntegration,
      formikHelpers: FormikHelpers<SuccessFactorsIntegration>
    ) => {
      toastOutboundSavedRef.current = true;
      await updateIntegration(
        {
          report_url_type: (values.report_url_type ||
            "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
          successfactors_integration: {
            ...optionalTeamIdPayload(values.team_id),
            ...effectiveTestConnectionPayload(values, clientParsedOutbound),
          },
        } as components["schemas"]["AtsIntegrationUpdate"],
        formikHelpers
      );
    },
    [clientParsedOutbound, updateIntegration]
  );

  const handleConnect = useCallback(
    async (
      values: SuccessFactorsIntegration,
      formikHelpers: FormikHelpers<SuccessFactorsIntegration>
    ) => {
      connectSucceededRef.current = false;
      await testConnection(
        {
          ...effectiveTestConnectionPayload(values, clientParsedOutbound),
          ...(createdIntegrationId
            ? { ats_integration_id: createdIntegrationId }
            : {}),
        },
        formikHelpers
      );
      if (!connectSucceededRef.current) {
        return;
      }
      if (createdIntegrationId) {
        outboundSaveSucceededRef.current = false;
        toastOutboundSavedRef.current = true;
        await updateIntegration(
          {
            report_url_type: (values.report_url_type ||
              "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
            successfactors_integration: persistPayload(
              values,
              clientParsedOutbound
            ),
          } as components["schemas"]["AtsIntegrationUpdate"],
          formikHelpers
        );
        if (!outboundSaveSucceededRef.current) {
          return;
        }
      }
      setCurrentStep(1);
    },
    [
      clientParsedOutbound,
      createdIntegrationId,
      testConnection,
      updateIntegration,
    ]
  );

  const handleSubmit = useCallback(
    async (
      values: SuccessFactorsIntegration,
      formikHelpers: FormikHelpers<SuccessFactorsIntegration>
    ) => {
      if (isEditMode) {
        return;
      }
      if (currentStep === 0) {
        if (hasRequiredOutboundValues(values, clientParsedOutbound)) {
          await handleConnect(values, formikHelpers);
        }
        return;
      }
      if (currentStep !== 1 || generatedCredentials) {
        return;
      }
      setIntegration(values);
      await createIntegration(
        {
          report_url_type: (values.report_url_type ||
            "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
          successfactors_integration: {
            team_id: values.team_id,
            ...effectiveTestConnectionPayload(values, clientParsedOutbound),
          },
        } as components["schemas"]["AtsIntegrationCreate"],
        formikHelpers
      );
    },
    [
      clientParsedOutbound,
      createIntegration,
      currentStep,
      generatedCredentials,
      handleConnect,
      isEditMode,
      setIntegration,
    ]
  );

  const handleTestConnection = useCallback(
    async (
      values: SuccessFactorsIntegration,
      formikHelpers: FormikHelpers<SuccessFactorsIntegration>
    ) => {
      await testConnection(
        {
          ...effectiveTestConnectionPayload(values, clientParsedOutbound),
          ...(initialValues?.id
            ? { ats_integration_id: initialValues.id }
            : {}),
        },
        formikHelpers
      );
    },
    [clientParsedOutbound, initialValues?.id, testConnection]
  );

  const isSubmitting = isCreating || isUpdating || isTestingConnection;
  const webhookPath =
    generatedCredentials?.webhook_url ||
    webhookPathFromIntegration(initialValues?.successfactors_integration);
  const webhookUrl = buildSuccessFactorsWebhookUrl(domain, webhookPath);
  const webhookUsername =
    generatedCredentials?.webhook_username ||
    initialValues?.successfactors_integration?.webhook_username ||
    "";
  const webhookPassword = generatedCredentials?.webhook_password || "";
  const showGeneratedSecrets = Boolean(generatedCredentials);
  const showCredentialFields = isEditMode || showGeneratedSecrets;
  const storedSuccessFactors = initialValues?.successfactors_integration as
    | SuccessFactorsRecord
    | undefined;

  const handleSapCertificateFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      setCertificateParseError(null);
      readSuccessFactorsCertificateFile(
        file,
        (parsed, fileName) => {
          const parsedOutbound: ClientParsedOutbound = {
            x509_certificate: parsed.certificatePem,
            saml_private_key: parsed.privateKeyPem,
            company_id: parsed.companyId ?? "",
            needsManualCompanyId: !parsed.companyId,
          };
          setNeedsManualCompanyId(parsedOutbound.needsManualCompanyId);
          setClientParsedOutbound(parsedOutbound);
          setCertificateUploadStatus(
            t("Read {{fileName}} — certificate parsed", { fileName })
          );
        },
        (message) => {
          setCertificateParseError(message);
          setCertificateUploadStatus(null);
          setClientParsedOutbound(null);
        },
        (fileName) => {
          setCertificateUploadStatus(t("Reading {{fileName}}", { fileName }));
        }
      );
    },
    [setClientParsedOutbound, setCertificateUploadStatus, t]
  );

  const inboundContent = (
    <SuccessFactorsInboundSection
      showCredentialFields={showCredentialFields}
      showGeneratedSecrets={showGeneratedSecrets}
      webhookUrl={webhookUrl}
      webhookUsername={webhookUsername}
      webhookPassword={webhookPassword}
      storedWebhookPassword={
        initialValues?.successfactors_integration?.webhook_password || ""
      }
      customerGroups={customerGroups}
      isCustomerGroupsLoading={isCustomerGroupsLoading}
      customerGroupsError={customerGroupsError}
      onRetryCustomerGroups={() => {
        void retryCustomerGroups();
      }}
      isTeamDisabled={isSubmitting || isEditMode || showGeneratedSecrets}
    />
  );

  return (
    <>
      <SuccessFactorsSapCertificateFileInput
        inputRef={sapCertificateInputRef}
        inputId={sapCertificateFileInputId}
        onChange={handleSapCertificateFileChange}
      />
      <Formik
        enableReinitialize={!isEditMode}
        validateOnMount
        initialValues={integrationData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          formikBagRef.current = formik;
          const { handleSubmit, values } = formik;

          const displayCompanyId = effectiveOutboundField(
            values,
            clientParsedOutbound,
            "company_id"
          );
          const displayX509Certificate = effectiveOutboundField(
            values,
            clientParsedOutbound,
            "x509_certificate"
          );
          const displaySamlPrivateKey = effectiveOutboundField(
            values,
            clientParsedOutbound,
            "saml_private_key"
          );
          const showOutboundCertificateMaterial =
            hasOutboundCertificateMaterial(
              values,
              clientParsedOutbound,
              storedSuccessFactors
            );
          const emptyMaterialPreview = t("(empty)");
          const x509MaterialPreview =
            maskedPemPreview(displayX509Certificate) || emptyMaterialPreview;
          const samlMaterialPreview =
            maskedPrivateKeyPreview(
              displaySamlPrivateKey,
              Boolean(storedSuccessFactors?.has_saml_private_key) &&
                !displaySamlPrivateKey.trim()
            ) || emptyMaterialPreview;

          const outboundContent = (
            <SuccessFactorsOutboundSection
              isSubmitting={isSubmitting}
              sapCertificateInputRef={sapCertificateInputRef}
              certificateUploadStatus={certificateUploadStatus}
              certificateParseError={certificateParseError}
              showOutboundCertificateMaterial={showOutboundCertificateMaterial}
              needsManualCompanyId={needsManualCompanyId}
              displayCompanyId={displayCompanyId}
              x509MaterialPreview={x509MaterialPreview}
              samlMaterialPreview={samlMaterialPreview}
            />
          );

          const outboundReady = hasRequiredOutboundValues(
            values,
            clientParsedOutbound
          );

          const createSteps = [
            {
              subtitle: t("Connect your SAP SuccessFactors account"),
              content: outboundContent,
              buttonLabel: t("Connect"),
              isDisabled: isSubmitting || !outboundReady,
              onComplete: () => {
                void handleConnect(values, formik);
              },
            },
            {
              subtitle: t("Generate inbound webhook credentials"),
              content: inboundContent,
              buttonLabel: showGeneratedSecrets
                ? t("Next")
                : t("Generate credentials"),
              isDisabled: showGeneratedSecrets
                ? isSubmitting
                : isSubmitting ||
                  !values.team_id.trim() ||
                  Boolean(customerGroupsError),
              onComplete: showGeneratedSecrets
                ? () => setCurrentStep(2)
                : () => handleSubmit(),
            },
            {
              subtitle: t("Copy your package and team IDs"),
              content: <SuccessFactorsPackagesAndTeamsList />,
              buttonLabel: t("Finish setup"),
              isDisabled: isSubmitting,
              onComplete: () => navigate("/settings/ats-integrations"),
            },
          ];
          const createStep = createSteps[currentStep];

          const formContent = (
            <Container
              layout="narrow"
              testID="successfactors-setup-page-outermost-container"
              className={styles.container}
            >
              <Form>
                {isEditMode && (
                  <Card type="large" className={styles.card}>
                    <Stack gap="space-4">
                      <div className={styles.mainContentContainer}>
                        {inboundContent}
                        <Stack
                          orientation="horizontal"
                          justify="end"
                          gap="space-4"
                        >
                          <BasicButton
                            testID="cancel-successfactors"
                            type="button"
                            isDisabled={isSubmitting}
                            onPress={() =>
                              navigate("/settings/ats-integrations")
                            }
                          >
                            {t("Cancel")}
                          </BasicButton>
                          <PrimaryButton
                            testID="regenerate-successfactors-credentials"
                            type="button"
                            isDisabled={
                              isSubmitting || !initialValues?.is_active
                            }
                            onPress={() => {
                              handleRegenerate(values, formik);
                            }}
                          >
                            {t("Regenerate credentials")}
                          </PrimaryButton>
                        </Stack>
                        <Divider />
                        {outboundContent}
                        <Stack
                          orientation="horizontal"
                          justify="end"
                          gap="space-4"
                        >
                          <BasicButton
                            testID="successfactors-test-connection"
                            type="button"
                            isDisabled={isSubmitting || !outboundReady}
                            onPress={() => {
                              handleTestConnection(values, formik);
                            }}
                          >
                            {t("Test connection")}
                          </BasicButton>
                          <PrimaryButton
                            testID="save-successfactors-outbound"
                            type="button"
                            isDisabled={
                              isSubmitting ||
                              !outboundReady ||
                              !initialValues?.is_active
                            }
                            onPress={() => {
                              handleSaveOutbound(values, formik);
                            }}
                          >
                            {t("Save outbound settings")}
                          </PrimaryButton>
                        </Stack>
                      </div>
                    </Stack>
                  </Card>
                )}

                {!isEditMode && (
                  <>
                    <Stack gap="space-2">
                      <Text
                        as="h1"
                        className={styles.titleSpacing}
                        style="global/title-small"
                      >
                        {t("SAP SuccessFactors Integration")}
                      </Text>
                      <Text
                        style="global/heading-medium"
                        testID="successfactors-setup-step-subtitle"
                      >
                        {createStep.subtitle}
                      </Text>
                    </Stack>
                    <div className={styles.mainContentContainer}>
                      {createStep.content}
                    </div>
                  </>
                )}
              </Form>
            </Container>
          );

          if (isEditMode) {
            return formContent;
          }

          return (
            <Layout
              layout="narrow"
              backgroundColor="surface-base"
              header={
                <SuccessFactorsIntegrationStepHeader
                  currentStep={currentStep}
                  buttonLabel={createStep.buttonLabel}
                  onComplete={createStep.onComplete}
                  onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
                  isDisabled={createStep.isDisabled}
                  submitting={isSubmitting}
                  confirmCancel={!createdIntegrationId}
                />
              }
            >
              <div className={styles.stepContainer}>{formContent}</div>
            </Layout>
          );
        }}
      </Formik>
    </>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
  stepContainer: css({
    display: "flex",
    justifyContent: "center",
  }),
  hiddenFileInput: css({
    display: "block",
    marginTop: "calc(var(--space-2) * -1)",
  }),
};
